import React from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import type { Specialization } from '@/types';

const SPECIALIZATION_GROUPS: { group: string; items: { value: Specialization; label: string }[] }[] = [
  {
    group: 'Home / Field Services',
    items: [
      { value: 'electrical', label: 'Electrical' },
      { value: 'plumbing', label: 'Plumbing' },
      { value: 'hvac', label: 'HVAC' },
      { value: 'security', label: 'Security' },
      { value: 'networking', label: 'Networking' },
      { value: 'solar', label: 'Solar' },
      { value: 'av', label: 'Audio/Visual' },
      { value: 'iot', label: 'IoT / Smart Home' },
      { value: 'lighting', label: 'Lighting' },
      { value: 'landscaping', label: 'Landscaping' },
      { value: 'automation', label: 'Automation' },
      { value: 'general', label: 'General' },
    ],
  },
  {
    group: 'Business Services',
    items: [
      { value: 'marketing', label: 'Marketing' },
      { value: 'sales', label: 'Sales' },
      { value: 'consulting', label: 'Consulting' },
      { value: 'business_strategy', label: 'Business Strategy' },
      { value: 'accounting', label: 'Accounting' },
    ],
  },
  {
    group: 'Tech Services',
    items: [
      { value: 'it_services', label: 'IT Services' },
      { value: 'web_development', label: 'Web Development' },
      { value: 'software', label: 'Software' },
      { value: 'cybersecurity', label: 'Cybersecurity' },
      { value: 'data_analytics', label: 'Data Analytics' },
    ],
  },
  {
    group: 'Creative Services',
    items: [
      { value: 'design', label: 'Design' },
      { value: 'graphic_design', label: 'Graphic Design' },
      { value: 'content_creation', label: 'Content Creation' },
      { value: 'photography', label: 'Photography' },
      { value: 'video_production', label: 'Video Production' },
    ],
  },
  {
    group: 'Other',
    items: [
      { value: 'other', label: 'Other' },
    ],
  },
];

export interface ServiceProviderProfileFormData {
  specializations: Specialization[];
  otherSpecialization?: string;
  businessName: string;
  businessDescription?: string;
  businessPhone: string;
  businessEmail: string;
  businessWebsite: string;
  employeeCount?: number;
  businessAddress: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country?: string;
  };
}

/** @deprecated Use ServiceProviderProfileFormData instead */
export type InstallerProfileFormData = ServiceProviderProfileFormData;

interface ServiceProviderProfileFormProps {
  onSubmit: (data: ServiceProviderProfileFormData) => void;
  defaultValues?: Partial<ServiceProviderProfileFormData>;
  isLoading?: boolean;
  submitLabel?: string;
}

export const ServiceProviderProfileForm: React.FC<ServiceProviderProfileFormProps> = ({
  onSubmit,
  defaultValues,
  isLoading = false,
  submitLabel = 'Create Profile',
}) => {
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<ServiceProviderProfileFormData>({
    defaultValues: {
      specializations: [],
      businessAddress: { street: '', city: '', state: '', postalCode: '' },
      ...defaultValues,
    },
  });

  const selectedSpecializations = watch('specializations') || [];

  const toggleSpecialization = (specialization: Specialization) => {
    const current = selectedSpecializations;
    const updated = current.includes(specialization)
      ? current.filter((s) => s !== specialization)
      : [...current, specialization];
    setValue('specializations', updated, { shouldValidate: true });
    if (specialization === 'other' && current.includes('other')) {
      setValue('otherSpecialization', '');
    }
  };

  const hasOtherSelected = selectedSpecializations.includes('other');

  const onFormSubmit = (data: ServiceProviderProfileFormData) => {
    if (data.specializations.length === 0) return;
    if (data.employeeCount) {
      data.employeeCount = Number(data.employeeCount);
    }
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Specializations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {SPECIALIZATION_GROUPS.map(({ group, items }) => (
            <div key={group}>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{group}</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {items.map(({ value, label }) => (
                  <div key={value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`specialization-${value}`}
                      checked={selectedSpecializations.includes(value)}
                      onCheckedChange={() => toggleSpecialization(value)}
                      disabled={isLoading}
                    />
                    <Label htmlFor={`specialization-${value}`} className="cursor-pointer text-sm">
                      {label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {hasOtherSelected && (
            <div className="mt-3">
              <Label htmlFor="otherSpecialization">Please specify your specialization</Label>
              <Input
                id="otherSpecialization"
                {...register('otherSpecialization')}
                placeholder="e.g. Pool Installation, Fire Protection"
                disabled={isLoading}
                className="mt-1"
              />
            </div>
          )}
          {selectedSpecializations.length === 0 && (
            <p className="mt-2 text-sm text-red-500">Select at least one specialization</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Business Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="businessName">Business Name <span className="text-red-500">*</span></Label>
            <Input
              id="businessName"
              {...register('businessName', { required: 'Business name is required' })}
              placeholder="Your business or company name"
              disabled={isLoading}
            />
            {errors.businessName && (
              <p className="mt-1 text-sm text-red-500">{errors.businessName.message}</p>
            )}
          </div>
          <div>
            <Label htmlFor="businessDescription">Description</Label>
            <Textarea
              id="businessDescription"
              {...register('businessDescription')}
              placeholder="Brief description of your services"
              disabled={isLoading}
              rows={3}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="businessPhone">Phone Number <span className="text-red-500">*</span></Label>
              <Input
                id="businessPhone"
                type="tel"
                {...register('businessPhone', { required: 'Phone number is required' })}
                placeholder="(555) 123-4567"
                disabled={isLoading}
              />
              {errors.businessPhone && (
                <p className="mt-1 text-sm text-red-500">{errors.businessPhone.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="businessEmail">Business Email <span className="text-red-500">*</span></Label>
              <Input
                id="businessEmail"
                type="email"
                {...register('businessEmail', { required: 'Business email is required' })}
                placeholder="contact@yourbusiness.com"
                disabled={isLoading}
              />
              {errors.businessEmail && (
                <p className="mt-1 text-sm text-red-500">{errors.businessEmail.message}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="businessWebsite">Website <span className="text-red-500">*</span></Label>
              <Input
                id="businessWebsite"
                {...register('businessWebsite', { required: 'Website is required' })}
                placeholder="https://yourbusiness.com"
                disabled={isLoading}
              />
              {errors.businessWebsite && (
                <p className="mt-1 text-sm text-red-500">{errors.businessWebsite.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="employeeCount">Number of Employees</Label>
              <Input
                id="employeeCount"
                type="number"
                min={1}
                {...register('employeeCount')}
                placeholder="e.g. 10"
                disabled={isLoading}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Business Address</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="street">Street Address <span className="text-red-500">*</span></Label>
            <Input
              id="street"
              {...register('businessAddress.street', { required: 'Street address is required' })}
              placeholder="123 Main St"
              disabled={isLoading}
            />
            {errors.businessAddress?.street && (
              <p className="mt-1 text-sm text-red-500">{errors.businessAddress.street.message}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="col-span-2 sm:col-span-1">
              <Label htmlFor="city">City <span className="text-red-500">*</span></Label>
              <Input
                id="city"
                {...register('businessAddress.city', { required: 'City is required' })}
                placeholder="City"
                disabled={isLoading}
              />
              {errors.businessAddress?.city && (
                <p className="mt-1 text-sm text-red-500">{errors.businessAddress.city.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="state">State <span className="text-red-500">*</span></Label>
              <Input
                id="state"
                {...register('businessAddress.state', { required: 'State is required' })}
                placeholder="State"
                disabled={isLoading}
              />
              {errors.businessAddress?.state && (
                <p className="mt-1 text-sm text-red-500">{errors.businessAddress.state.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="postalCode">Zip Code <span className="text-red-500">*</span></Label>
              <Input
                id="postalCode"
                {...register('businessAddress.postalCode', { required: 'Zip code is required' })}
                placeholder="12345"
                disabled={isLoading}
              />
              {errors.businessAddress?.postalCode && (
                <p className="mt-1 text-sm text-red-500">{errors.businessAddress.postalCode.message}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Button type="submit" disabled={isLoading || selectedSpecializations.length === 0} className="w-full">
        {isLoading ? 'Saving...' : submitLabel}
      </Button>
    </form>
  );
};

/** @deprecated Use ServiceProviderProfileForm instead */
export const InstallerProfileForm = ServiceProviderProfileForm;
